import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bot, ChevronDown, ChevronRight, X } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';

const BASE = () => (import.meta.env.VITE_SUPABASE_URL as string) ?? 'https://beta.crm-api.primetecia.com.br/api';

type Grupo = 'carteira' | 'primeira' | 'entrega';
type Tom = 'divida' | 'neutro' | 'desfecho';

interface RoboFaturas {
  vigia: { codigo: string; rotulo: string; ok: boolean; verificadoEm: string | null; detalhe: string | null }[];
  regua: { grupo: Grupo; titulo: string; linhas: string[] }[];
  grupos: { grupo: Grupo; titulo: string; total: number; motivos: { chave: string; rotulo: string; tom: Tom; total: number }[] }[];
  hoje: { pedimos: number; contaNova: number; copiaSemIa: number; copiaLida: number; recusada: number };
  geradoEm: string;
}
interface ItemLista { id: string; empresa: string; documento: string; dono: string | null; detalhe: string; rota: string }
interface Lista { total: number; itens: ItemLista[]; geradoEm: string }

async function pedir<T>(caminho: string, falha: string): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Sessão expirada — faça login novamente.');
  const r = await fetch(`${BASE()}/v1/faturas/${caminho}`, { headers: { Authorization: `Bearer ${session.access_token}` } });
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(body?.error || falha);
  return body.data as T;
}

const fmt = (v: number) => v.toLocaleString('pt-BR');
const hora = (iso: string | null) => (iso ? new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' }) : '—');

const VERDE = 'text-[#075E54] dark:text-[#25D366]';
const BORDO = 'text-[#7A2235] dark:text-[#C87484]';
const COR: Record<Tom, string> = { divida: BORDO, neutro: '', desfecho: VERDE };
const LISTA_GRID = 'grid grid-cols-[minmax(0,1fr)_104px_112px_minmax(0,0.9fr)_52px] items-center gap-2';

/**
 * O ROBÔ DAS CONTAS E O QUE FALTA (Douglas 04/10/2026) — abre por um botão no
 * painel das Faturas do topo, que segue igual. O vigia, a régua escrita e cada
 * conta em UM motivo; o motivo abre a lista das empresas com o atalho para a
 * tela onde se resolve. Números e listas: /v1/faturas/robo (robo-faturas.service.ts).
 */
export function PainelRoboFaturas({ aberto, onFechar, onIr }: { aberto: boolean; onFechar: () => void; onIr: (rota: string) => void }) {
  const [aberta, setAberta] = useState<{ grupo: Grupo; motivo: string } | null>(null);
  const robo = useQuery({
    queryKey: ['faturas', 'robo'],
    enabled: aberto,
    staleTime: 60_000,
    refetchInterval: 2 * 60_000,
    queryFn: () => pedir<RoboFaturas>('robo', 'Não foi possível ler o robô das contas.'),
  });
  const lista = useQuery({
    queryKey: ['faturas', 'robo', 'lista', aberta?.grupo, aberta?.motivo],
    enabled: aberto && !!aberta,
    staleTime: 60_000,
    queryFn: () => pedir<Lista>(`robo/lista?grupo=${aberta!.grupo}&motivo=${aberta!.motivo}`, 'Não foi possível abrir a lista.'),
  });
  const d = robo.data;

  const th = 'text-[10px] font-bold uppercase tracking-[0.6px] text-muted-foreground';
  const caixa = 'rounded-xl border border-border bg-card px-3 py-2.5';
  const titulo = 'mb-1.5 text-[10px] font-bold uppercase tracking-[1px] text-muted-foreground';
  const linhaTrava = 'flex w-full items-center justify-between gap-2 rounded-md px-1 py-[3px] text-left text-[12px] hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40';
  const vigiaOk = d?.vigia.every((v) => v.ok) ?? false;

  return (
    <Dialog open={aberto} onOpenChange={(v) => { if (!v) onFechar(); }}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-[860px] max-h-[90vh] flex flex-col overflow-hidden p-0 gap-0 rounded-2xl sm:rounded-2xl bg-background [&>button]:hidden">
        <div className="flex shrink-0 items-center gap-3 bg-[linear-gradient(135deg,#4A1320_0%,#5B0A0D_55%,#6E1626_100%)] px-4 py-3">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[9px] bg-white/[0.12] text-white"><Bot className="h-4 w-4" /></span>
          <div className="min-w-0 flex-1">
            <DialogTitle className="font-display text-[15px] font-bold tracking-[-0.3px] text-white">Robô das contas · o que falta</DialogTitle>
            <p className="truncate text-[10.5px] text-white/65">
              {d ? `Situação de agora · atualizada às ${hora(d.geradoEm)}` : 'CNPJ · todas as equipes'}
            </p>
          </div>
          {d && (
            <span className="flex shrink-0 items-center gap-1.5 rounded-lg bg-white/[0.12] px-2 py-[3px] text-[11px] text-white">
              <i className={cn('inline-block h-2 w-2 rounded-full', vigiaOk ? 'bg-[#25D366] shadow-[0_0_0_3px_rgba(37,211,102,0.16)]' : 'bg-[#E88A8A]')} />
              {vigiaOk ? 'Vigia: tudo rodando' : 'Vigia: tem ponto parado'}
            </span>
          )}
          <button type="button" aria-label="Fechar" onClick={onFechar}
            className="grid h-[26px] w-[26px] place-items-center rounded-lg border border-white/[0.14] bg-white/[0.12] text-white hover:bg-white/[0.22]">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-2.5 overflow-y-auto px-4 py-3">
          {robo.isLoading && <p className="text-[11px] text-muted-foreground">Lendo o robô das contas…</p>}
          {robo.error && (
            <p role="alert" className="text-[11px] text-destructive">
              {(robo.error as Error).message}{' '}
              <button type="button" className="underline" onClick={() => void robo.refetch()}>Tentar de novo</button>
            </p>
          )}
          {d && <>
            <div className={caixa}>
              <p className={titulo}>O robô agora · vigia do ciclo</p>
              <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
                {d.vigia.map((v) => (
                  <p key={v.codigo} className="flex items-center justify-between gap-2 py-[3px] text-[12px]">
                    <span className="flex min-w-0 items-center gap-1.5">
                      <i className={cn('inline-block h-2 w-2 shrink-0 rounded-full', v.ok ? 'bg-[#25D366]' : 'bg-[#E88A8A]')} />
                      <span className="truncate" title={v.rotulo}>{v.rotulo}</span>
                    </span>
                    <span className={cn('shrink-0 tabular-nums text-[11px]', v.ok ? 'text-muted-foreground' : cn('font-semibold', BORDO))}>
                      {v.ok ? `ok às ${hora(v.verificadoEm)}` : `${v.detalhe || 'parado'} · às ${hora(v.verificadoEm)}`}
                    </span>
                  </p>
                ))}
              </div>
            </div>

            <div className={caixa}>
              <p className={titulo}>Como o robô pede · a mesma régua que ele executa</p>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                {d.regua.map((r) => (
                  <div key={r.grupo}>
                    <p className="mb-0.5 text-[12px] font-semibold">{r.titulo}</p>
                    <ul className="space-y-0.5">
                      {r.linhas.map((l) => <li key={l} className="text-[11.5px] leading-snug text-muted-foreground">{l}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {d.grupos.map((g) => (
              <div key={g.grupo} className={caixa}>
                <p className={titulo}>{g.titulo} · {fmt(g.total)} {g.grupo === 'carteira' ? 'empresas' : 'contas'}</p>
                {g.motivos.map((m) => {
                  const esta = aberta?.grupo === g.grupo && aberta.motivo === m.chave;
                  return (
                    <div key={m.chave}>
                      <button type="button" aria-expanded={esta} disabled={m.total === 0}
                        onClick={() => setAberta(esta ? null : { grupo: g.grupo, motivo: m.chave })}
                        className={cn(linhaTrava, m.total === 0 && 'cursor-default hover:bg-transparent')}>
                        <span className="flex min-w-0 items-center gap-1">
                          {m.total > 0 && (esta ? <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" /> : <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" />)}
                          <span className="truncate">{m.rotulo}</span>
                        </span>
                        <span className={cn('shrink-0 font-display text-[13.5px] tabular-nums', m.total > 0 && 'font-semibold', m.total > 0 ? COR[m.tom] : 'text-muted-foreground')}>{fmt(m.total)}</span>
                      </button>
                      {esta && (
                        <div className="mb-1.5 ml-3 border-l-[3px] border-l-[#7A2235] pl-2 dark:border-l-[#C87484]">
                          {lista.isLoading && <p className="py-1 text-[11px] text-muted-foreground">Abrindo a lista…</p>}
                          {lista.error && <p role="alert" className="py-1 text-[11px] text-destructive">{(lista.error as Error).message}</p>}
                          {lista.data && <>
                            <div className={cn(LISTA_GRID, 'py-1')}>
                              <span className={th}>Empresa</span><span className={th}>{g.grupo === 'carteira' ? 'CNPJ' : 'Pedido'}</span>
                              <span className={th}>Dono</span><span className={th}>Motivo</span><span />
                            </div>
                            {lista.data.itens.map((i) => (
                              <div key={i.id} className={cn(LISTA_GRID, 'h-8 border-t border-border/60 text-[12px]')}>
                                <span className="truncate font-medium" title={i.empresa}>{i.empresa}</span>
                                <span className="truncate tabular-nums text-muted-foreground" title={i.documento}>{i.documento}</span>
                                <span className="truncate text-muted-foreground" title={i.dono || 'sem dono'}>{i.dono || 'sem dono'}</span>
                                <span className="truncate text-muted-foreground" title={i.detalhe}>{i.detalhe || '—'}</span>
                                <button type="button" onClick={() => onIr(i.rota)}
                                  className="h-[24px] rounded-md border border-border px-2 text-[11px] font-semibold text-primary hover:bg-muted/50">Abrir</button>
                              </div>
                            ))}
                            {lista.data.total > lista.data.itens.length && (
                              <p className="py-1 text-[11px] text-muted-foreground">Mostrando {fmt(lista.data.itens.length)} de {fmt(lista.data.total)}.</p>
                            )}
                          </>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}

            <div className="flex items-center gap-2">
              <p className="w-[44px] shrink-0 text-[10px] font-bold uppercase tracking-[1px] text-muted-foreground">Hoje</p>
              <div className="grid flex-1 grid-cols-3 gap-1.5 sm:grid-cols-5">
                {([
                  ['Pedimos ao Bee', d.hoje.pedimos, ''],
                  ['Conta nova', d.hoje.contaNova, VERDE],
                  ['Cópia · sem IA', d.hoje.copiaSemIa, 'text-muted-foreground'],
                  ['Cópia lida pela IA', d.hoje.copiaLida, d.hoje.copiaLida > 0 ? BORDO : 'text-muted-foreground'],
                  ['Recusada', d.hoje.recusada, d.hoje.recusada > 0 ? BORDO : 'text-muted-foreground'],
                ] as const).map(([r, v, cor]) => (
                  <div key={r} className="rounded-lg bg-[#FAF8F4] px-2 py-1.5 dark:bg-[#241A1A]">
                    <p className="truncate text-[10px] text-muted-foreground">{r}</p>
                    <p className={cn('font-display text-[16px] font-bold tabular-nums', cor)}>{fmt(v)}</p>
                  </div>
                ))}
              </div>
            </div>

            <p className="text-[11px] leading-relaxed text-muted-foreground">
              Cada empresa ou conta está em um motivo só: a soma de cada quadro é o total dele. É a situação do robô agora, para o próximo pedido;
              o painel das Faturas mostra o mês da conta. "Cópia lida pela IA" é cópia cujo texto não deu para reconhecer antes da leitura.
            </p>
          </>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
